
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Image, Eye, EyeOff, X } from "lucide-react";
import { supabase } from '@/integrations/supabase/client';
import { useToast } from "@/hooks/use-toast";
import ImageCropper from '../profile/ImageCropper';
import { fixNativeIosViewportAfterOverlay } from '@/utils/iosViewport';

interface CoverImageProps {
  coverImage: string | null;
  onCoverImageUpload: (url: string) => void;
}

const CoverImage: React.FC<CoverImageProps> = ({ coverImage, onCoverImageUpload }) => {
  const { toast } = useToast();
  const [isVisible, setIsVisible] = useState(!!coverImage);
  const [isDragging, setIsDragging] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file",
        description: "Please select an image file",
        variant: "destructive",
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setSelectedImage(e.target?.result as string);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    handleFileSelect(file);
    // Reset iOS viewport after file picker closes
    fixNativeIosViewportAfterOverlay();
  };

  const handleUploadAreaClick = () => {
    document.getElementById('cover-upload')?.click();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleCrop = async (croppedBlob: Blob) => {
    setUploading(true);
    window.dispatchEvent(new Event('fileUploadStart'));

    try {
      const fileExt = 'png';
      const filePath = `${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('note-covers')
        .upload(filePath, croppedBlob);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('note-covers')
        .getPublicUrl(filePath);

      onCoverImageUpload(publicUrl);
      setIsVisible(true);
      setShowCropper(false);
      setSelectedImage(null);
      
      toast({
        title: "Cover image uploaded",
        description: "Your cover image has been uploaded successfully.",
      });
      // Reset iOS viewport after upload completes
      fixNativeIosViewportAfterOverlay();
    } catch (error: any) {
      console.error('Error uploading cover image:', error);
      toast({
        title: "Upload failed",
        description: error.message || "Failed to upload cover image",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      window.dispatchEvent(new Event('fileUploadEnd'));
    }
  };

  const handleRemove = () => {
    onCoverImageUpload("");
    setIsVisible(false);
  };

  return (
    <>
      <div className="relative w-full mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium">Cover Image</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsVisible(!isVisible)}
            className="text-xs h-8 px-2"
          >
            {isVisible ? <Eye className="h-4 w-4 mr-1" /> : <EyeOff className="h-4 w-4 mr-1" />}
            {isVisible ? 'Hide' : 'Upload'}
          </Button>
        </div>

        {isVisible && (
          <>
            {coverImage ? (
              <div className="relative w-full aspect-[4/3] overflow-hidden rounded-lg mb-2">
                <img
                  src={coverImage}
                  alt="Cover"
                  className="w-full h-full object-cover"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-2 bg-background/80 hover:bg-background/90"
                  onClick={handleRemove}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div
                className={`w-full aspect-[4/3] flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg transition-colors ${
                  isDragging 
                    ? 'border-primary bg-primary/5' 
                    : 'border-border hover:border-primary/50'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleUploadAreaClick}
              >
                <Image className="w-8 h-8 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {isDragging ? 'Drop image here' : 'Click or drag & drop to add cover image'}
                </span>
              </div>
            )}
          </>
        )}

        <input
          type="file"
          id="cover-upload"
          accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/heic,image/heif"
          className="hidden"
          onChange={handleFileUpload}
        />
      </div>

      <ImageCropper
        open={showCropper}
        onOpenChange={setShowCropper}
        selectedImage={selectedImage}
        onCrop={handleCrop}
        uploading={uploading}
        cropAspectRatio={4 / 3}
        dialogTitle="Crop Cover Image"
      />

    </>
  );
};

export default CoverImage;
